import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FitnessCenter as EquipmentIcon,
  DirectionsRun as ExerciseIcon,
  Search as SearchIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  Launch as LaunchIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GymService } from '../services/gymService';
import type { Gym, CreateGymInput } from '../services/types';
import { API_BASE_URL as API_URL } from '../config';
import { apiClient } from '../services/api';

interface GymEquipment {
  id: number;
  name: string;
  type: string;
  description?: string;
  image?: string;
  muscleGroups?: string[];
}

interface GymExercise {
  id: number;
  name: string;
  category?: string;
  muscleGroup?: string;
  difficulty?: string;
  equipment?: string;
}

const equipmentTypeLabels: Record<string, string> = {
  free_weight: 'Свободна тежест',
  machine: 'Машина',
  mat: 'Стелка',
  box: 'Бокс',
  other: 'Друго',
};

const GYM_LIST_PAGE_SIZE = 50;
const GYM_TYPE_OPTIONS = [
  { value: 'commercial_gym', label: 'Commercial Gym' },
  { value: 'home_gym', label: 'Home Gym' },
  { value: 'office_gym', label: 'Office Gym' },
  { value: 'hotel_gym', label: 'Hotel Gym' },
  { value: 'outside_gym', label: 'Outside Gym' },
];
const GYM_TYPE_LABELS = GYM_TYPE_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

interface AvailableEquipment {
  id: number;
  name: string;
}

const GymManager: React.FC = () => {
  const navigate = useNavigate();
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGym, setEditingGym] = useState<Gym | null>(null);
  const [formData, setFormData] = useState<CreateGymInput>({ name: '', address: '', type: 'commercial_gym', equipment: [] });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchQuery, setSearchQuery] = useState('');
  const [allEquipment, setAllEquipment] = useState<AvailableEquipment[]>([]);

  // Expanded gym view state
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [selectedView, setSelectedView] = useState<'equipment' | 'exercises' | null>(null);
  const [gymEquipment, setGymEquipment] = useState<GymEquipment[]>([]);
  const [gymExercises, setGymExercises] = useState<GymExercise[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [equipmentOffset, setEquipmentOffset] = useState(0);
  const [exerciseOffset, setExerciseOffset] = useState(0);
  const [equipmentHasMore, setEquipmentHasMore] = useState(false);
  const [exerciseHasMore, setExerciseHasMore] = useState(false);

  // Delete confirmation dialog state
  const [gymToDelete, setGymToDelete] = useState<Gym | null>(null);

  // Temp IDs for optimistic created records.
  const tempGymIdRef = useRef(-1);

  useEffect(() => {
    fetchGyms();
    fetchAssignableEquipment();
  }, []);

  const fetchGyms = async () => {
    setLoading(true);
    try {
      const data = await GymService.getAll();
      setGyms(data);
    } catch (error) {
      console.error('Error fetching gyms:', error);
      showSnackbar('Грешка при зареждане на залите', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableEquipment = async () => {
    try {
      const response = await apiClient.get<any>('/api/equipment?itemsPerPage=1000');
      const collection = response.data?.['hydra:member'] || response.data?.member || [];
      const normalized = Array.isArray(collection)
        ? collection
          .filter((item) => item && typeof item.id === 'number')
          .map((item) => ({ id: item.id, name: item.name as string }))
        : [];
      setAllEquipment(normalized);
    } catch (error) {
      console.error('Error fetching equipment for gym assignment:', error);
      showSnackbar('Грешка при зареждане на оборудването за зала', 'error');
    }
  };

  const normalizeGymEquipmentIris = (gym: Gym): string[] => {
    if (!Array.isArray(gym.equipment)) {
      return [];
    }

    return gym.equipment
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item?.id) {
          return `/api/equipment/${item.id}`;
        }
        return null;
      })
      .filter((item): item is string => typeof item === 'string');
  };

  const handleOpenDialog = (gym?: Gym) => {
    if (gym) {
      setEditingGym(gym);
      setFormData({
        name: gym.name,
        address: gym.address || '',
        type: gym.type || 'commercial_gym',
        equipment: normalizeGymEquipmentIris(gym),
      });
    } else {
      setEditingGym(null);
      setFormData({ name: '', address: '', type: 'commercial_gym', equipment: [] });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGym(null);
    setFormData({ name: '', address: '', type: 'commercial_gym', equipment: [] });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Моля, въведете име на залата', 'error');
      return;
    }

    setSaving(true);
    let optimisticCreatedGymId: number | null = null;
    try {
      if (editingGym) {
        const optimisticGym: Gym = {
          ...editingGym,
          name: formData.name.trim(),
          address: formData.address?.trim() || '',
          type: formData.type || 'commercial_gym',
          equipment: formData.equipment || [],
        };

        setGyms((prev) => prev.map((gym) => (gym.id === editingGym.id ? optimisticGym : gym)));
        if (selectedGym?.id === editingGym.id) {
          setSelectedGym(optimisticGym);
        }

        const updatedGym = await GymService.update(editingGym.id, formData);
        setGyms((prev) => prev.map((gym) => (gym.id === editingGym.id ? updatedGym : gym)));
        if (selectedGym?.id === editingGym.id) {
          setSelectedGym(updatedGym);
        }
        showSnackbar('Залата е актуализирана успешно', 'success');
      } else {
        const tempGym: Gym = {
          id: tempGymIdRef.current--,
          name: formData.name.trim(),
          address: formData.address?.trim() || '',
          type: formData.type || 'commercial_gym',
          equipment: formData.equipment || [],
          createdAt: new Date().toISOString(),
        };
        optimisticCreatedGymId = tempGym.id;

        setGyms((prev) => [tempGym, ...prev]);
        const createdGym = await GymService.create(formData);
        setGyms((prev) => prev.map((gym) => (gym.id === tempGym.id ? createdGym : gym)));
        showSnackbar('Залата е добавена успешно', 'success');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving gym:', error);
      if (editingGym) {
        setGyms((prev) => prev.map((gym) => (gym.id === editingGym.id ? editingGym : gym)));
        if (selectedGym?.id === editingGym.id) {
          setSelectedGym(editingGym);
        }
      } else if (optimisticCreatedGymId !== null) {
        setGyms((prev) => prev.filter((gym) => gym.id !== optimisticCreatedGymId));
      }
      showSnackbar('Грешка при запазване на залата', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (gym: Gym) => {
    setGymToDelete(gym);
  };

  const closeDeleteDialog = () => {
    setGymToDelete(null);
  };

  const handleDelete = async () => {
    if (!gymToDelete) {
      return;
    }

    const deletedGym = gymToDelete;
    const previousGyms = gyms;
    const previousSelectedGym = selectedGym;
    const previousSelectedView = selectedView;
    const previousEquipment = gymEquipment;
    const previousExercises = gymExercises;

    setGyms((prev) => prev.filter((gym) => gym.id !== deletedGym.id));
    if (selectedGym?.id === deletedGym.id) {
      setSelectedGym(null);
      setSelectedView(null);
      setGymEquipment([]);
      setGymExercises([]);
    }
    closeDeleteDialog();

    try {
      await GymService.delete(deletedGym.id);
      showSnackbar('Залата е изтрита успешно', 'success');
    } catch (error) {
      console.error('Error deleting gym:', error);
      setGyms(previousGyms);
      setSelectedGym(previousSelectedGym);
      setSelectedView(previousSelectedView);
      setGymEquipment(previousEquipment);
      setGymExercises(previousExercises);
      showSnackbar('Грешка при изтриване на залата', 'error');
    }
  };

  const handleViewEquipment = async (gym: Gym) => {
    if (selectedGym?.id === gym.id && selectedView === 'equipment') {
      setSelectedGym(null);
      setSelectedView(null);
      setGymEquipment([]);
      setEquipmentOffset(0);
      setEquipmentHasMore(false);
      return;
    }
    setSelectedGym(gym);
    setSelectedView('equipment');
    setGymExercises([]);
    setExerciseOffset(0);
    setExerciseHasMore(false);
    setLoadingEquipment(true);
    try {
      const response = await GymService.getGymEquipment(gym.id, GYM_LIST_PAGE_SIZE, 0);
      const equipment = Array.isArray(response?.equipment) ? response.equipment : [];
      setGymEquipment(equipment);
      setEquipmentOffset((response.offset ?? 0) + equipment.length);
      setEquipmentHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error fetching gym equipment:', error);
      showSnackbar('Грешка при зареждане на оборудването', 'error');
    } finally {
      setLoadingEquipment(false);
    }
  };

  const normalizeGymExercisesResponse = (response: any): GymExercise[] => {
    if (Array.isArray(response)) {
      return response;
    }
    if (response?.['hydra:member'] && Array.isArray(response['hydra:member'])) {
      return response['hydra:member'];
    }
    if (response?.member && Array.isArray(response.member)) {
      return response.member;
    }
    if (response?.exercises && Array.isArray(response.exercises)) {
      return response.exercises;
    }
    return [];
  };

  const handleViewExercises = async (gym: Gym) => {
    if (selectedGym?.id === gym.id && selectedView === 'exercises') {
      setSelectedGym(null);
      setSelectedView(null);
      setGymExercises([]);
      setExerciseOffset(0);
      setExerciseHasMore(false);
      return;
    }
    setSelectedGym(gym);
    setSelectedView('exercises');
    setGymEquipment([]);
    setEquipmentOffset(0);
    setEquipmentHasMore(false);
    setLoadingExercises(true);
    try {
      const response = await GymService.getGymExercises(gym.id, GYM_LIST_PAGE_SIZE, 0);
      const exercises = normalizeGymExercisesResponse(response);
      setGymExercises(exercises);
      setExerciseOffset((response.offset ?? 0) + exercises.length);
      setExerciseHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error fetching gym exercises:', error);
      showSnackbar('Грешка при зареждане на упражненията', 'error');
    } finally {
      setLoadingExercises(false);
    }
  };

  const handleLoadMoreEquipment = async () => {
    if (!selectedGym || loadingEquipment || !equipmentHasMore) {
      return;
    }

    setLoadingEquipment(true);
    try {
      const response = await GymService.getGymEquipment(selectedGym.id, GYM_LIST_PAGE_SIZE, equipmentOffset);
      const nextEquipment = Array.isArray(response?.equipment) ? response.equipment : [];
      setGymEquipment((prev) => [...prev, ...nextEquipment]);
      setEquipmentOffset((response.offset ?? equipmentOffset) + nextEquipment.length);
      setEquipmentHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error loading more equipment:', error);
      showSnackbar('Грешка при зареждане на още оборудване', 'error');
    } finally {
      setLoadingEquipment(false);
    }
  };

  const handleLoadMoreExercises = async () => {
    if (!selectedGym || loadingExercises || !exerciseHasMore) {
      return;
    }

    setLoadingExercises(true);
    try {
      const response = await GymService.getGymExercises(selectedGym.id, GYM_LIST_PAGE_SIZE, exerciseOffset);
      const nextExercises = normalizeGymExercisesResponse(response);
      setGymExercises((prev) => [...prev, ...nextExercises]);
      setExerciseOffset((response.offset ?? exerciseOffset) + nextExercises.length);
      setExerciseHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error loading more exercises:', error);
      showSnackbar('Грешка при зареждане на още упражнения', 'error');
    } finally {
      setLoadingExercises(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredGyms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return gyms;
    }

    return gyms.filter((gym) => {
      const name = gym.name.toLowerCase();
      const address = gym.address?.toLowerCase() || '';
      return name.includes(query) || address.includes(query);
    });
  }, [gyms, searchQuery]);

  const equipmentIriToName = useMemo(() => {
    const byIri: Record<string, string> = {};
    allEquipment.forEach((item) => {
      byIri[`/api/equipment/${item.id}`] = item.name;
    });
    return byIri;
  }, [allEquipment]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Зали
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добави зала
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Търси по име или адрес..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : gyms.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Все още няма добавени зали. Добавете първата си зала!
          </Typography>
        </Box>
      ) : filteredGyms.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Няма резултати за "{searchQuery}".
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredGyms.map((gym) => (
            <Card
              key={gym.id}
              sx={{
                border: selectedGym?.id === gym.id ? '2px solid' : '1px solid',
                borderColor: selectedGym?.id === gym.id ? 'primary.main' : 'divider',
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {gym.name}
                    </Typography>
                    {gym.address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {gym.address}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={GYM_TYPE_LABELS[gym.type || 'commercial_gym'] || gym.type || 'commercial_gym'}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(gym)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => openDeleteDialog(gym)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<EquipmentIcon />}
                  onClick={() => handleViewEquipment(gym)}
                  variant={selectedGym?.id === gym.id && selectedView === 'equipment' ? 'contained' : 'outlined'}
                >
                  Оборудване
                </Button>
                <Button
                  size="small"
                  startIcon={<ExerciseIcon />}
                  onClick={() => handleViewExercises(gym)}
                  variant={selectedGym?.id === gym.id && selectedView === 'exercises' ? 'contained' : 'outlined'}
                >
                  Упражнения
                </Button>
              </CardActions>

              {/* Expanded section - equipment or exercises */}
              {selectedGym?.id === gym.id && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  {selectedView === 'equipment' && (
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Оборудване в {gym.name}
                    </Typography>
                  )}
                  {selectedView === 'exercises' && (
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Упражнения в {gym.name}
                    </Typography>
                  )}
                  {(selectedView === 'equipment' && loadingEquipment) || (selectedView === 'exercises' && loadingExercises) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : selectedView === 'equipment' && gymEquipment.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Все още няма добавено оборудване. Редактирайте залата и добавете оборудване от формата.
                    </Typography>
                  ) : selectedView === 'exercises' && gymExercises.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Няма налични упражнения за тази зала.
                    </Typography>
                  ) : selectedView === 'equipment' ? (
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {gymEquipment.map((eq) => (
                          <Card key={eq.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33% - 8px)' } }}>
                            <CardActionArea onClick={() => navigate(`/equipment?edit=${eq.id}`)}>
                              {eq.image ? (
                                <Box
                                  component="img"
                                  src={`${API_URL}${eq.image}`}
                                  alt={eq.name}
                                  sx={{ width: '100%', height: 100, objectFit: 'cover' }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: 100,
                                    bgcolor: 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <ImageNotSupportedIcon color="disabled" />
                                </Box>
                              )}
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography variant="subtitle2" noWrap>{eq.name}</Typography>
                                  <LaunchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {equipmentTypeLabels[eq.type] || eq.type}
                                </Typography>
                                {eq.muscleGroups && eq.muscleGroups.length > 0 && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.5 }}>
                                    {eq.muscleGroups.map((mg) => (
                                      <Chip key={mg} label={mg} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    ))}
                                  </Box>
                                )}
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        ))}
                      </Box>
                      {equipmentHasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleLoadMoreEquipment}
                            disabled={loadingEquipment}
                          >
                            Зареди още
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {gymExercises.map((exercise) => (
                          <Card
                            key={exercise.id}
                            sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33% - 8px)' } }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1 } }}>
                              <Typography variant="subtitle2" noWrap>{exercise.name}</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {exercise.muscleGroup && (
                                  <Chip label={exercise.muscleGroup} size="small" sx={{ height: 20 }} />
                                )}
                                {exercise.difficulty && (
                                  <Chip label={exercise.difficulty} size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                                )}
                                {exercise.category && (
                                  <Chip label={exercise.category} size="small" variant="outlined" sx={{ height: 20 }} />
                                )}
                                {exercise.equipment && (
                                  <Chip label={exercise.equipment} size="small" variant="outlined" sx={{ height: 20 }} />
                                )}
                              </Box>
                              {!exercise.muscleGroup && !exercise.difficulty && !exercise.category && !exercise.equipment && (
                                <Typography variant="caption" color="text.secondary">
                                  Няма допълнителни данни за упражнението
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                      {exerciseHasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleLoadMoreExercises}
                            disabled={loadingExercises}
                          >
                            Зареди още
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          ))}
        </Stack>
      )}

      {/* Add/Edit Gym Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGym ? 'Редактиране на зала' : 'Добавяне на зала'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Име на залата"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="напр. Pulse Fitness, NextLevel Gym"
            />
            <TextField
              label="Адрес"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              placeholder="напр. бул. Витоша 100, София"
            />
            <FormControl fullWidth required>
              <InputLabel>Тип зала</InputLabel>
              <Select
                value={formData.type || 'commercial_gym'}
                label="Тип зала"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                {GYM_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {allEquipment.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Оборудване</InputLabel>
                <Select
                  multiple
                  value={formData.equipment || []}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({
                      ...formData,
                      equipment: typeof value === 'string' ? value.split(',') : value,
                    });
                  }}
                  input={<OutlinedInput label="Оборудване" />}
                  renderValue={(selected) => selected.map((iri) => equipmentIriToName[iri] || iri).join(', ')}
                >
                  {allEquipment.map((item) => {
                    const iri = `/api/equipment/${item.id}`;
                    return (
                      <MenuItem key={item.id} value={iri}>
                        <Checkbox checked={(formData.equipment || []).includes(iri)} />
                        <ListItemText primary={item.name} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Отказ
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Запазване...' : 'Запази'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(gymToDelete)} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Изтриване на зала</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Сигурни ли сте, че искате да изтриете "{gymToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Отказ</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymManager;
